import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import ChariotConnect from 'react-chariot-connect'

import csrfHeaders from 'src/utils/csrfHeaders'

import BackLink from '../BackLink'
import DonationExtras from '../DonationExtras'
import PageHeader from '../PageHeader'

// Final payment step for DAF donations. The Chariot Connect widget
// renders inline; DonationExtras (tribute / business_name / comment,
// each a modal trigger) sits above it; the back button sits below.
// No DataSharing — Chariot's site has its own privacy controls. And because
// there's no DataSharing here, there's deliberately no plugin
// `afterDataSharing` slot on this step (unlike Contact / CryptoForm /
// ExpressCheckout): DAF donations collect no contact/data-sharing info on our
// form, so a plugin scoped only to that slot doesn't run for DAF. See the
// "Slots aren't universal" note in the snippet editor tips.
//
// On mount we POST to `initUrl` with the org's EIN to get the Chariot
// `connect_id` + `api_key` we need to render the <ChariotConnect />
// button. saveDraft is NOT fired on mount — Chariot's onSuccess only
// runs after the donor's commit, and our handler controls everything
// from there, so we can save the draft sequentially inside onSuccess
// (same pattern as every other v7 payment method). Stock had to do
// otherwise because DonateStock's onDonationFormOpen fires *between*
// the donor's click and their commit, with no return-value contract
// to await on.
//
// onDonationRequest fires when the donor clicks the Chariot button and
// returns synchronously with any prefill data we have for the workflow.
// We don't collect anything pre-Chariot, so it returns `{}`. The
// donor's identity + amount come back through onSuccess once they've
// completed Chariot's flow.
//
// onSuccess saveDrafts then POSTs to
// PaymentProcessors::DafDonationsController#create with the
// resulting order_id + the Chariot payload. The controller runs
// ChariotApi.create_grant inline (Chariot is sync) and queues the
// confirmation mailer. :direct orgs finish at :processed; special_daf
// orgs rest at :pledged for staff conversion. No `pending: true` on the
// ThankYou result either way — the amount + email are known by the time
// we navigate.
const DafPayment = ({
  i18n, record, partnerItem, extras,
  dafProps, finished, setSubmitInFlight, onBack,
  blank_modal_src, emoji_picker_loader_src, widget_instance_token,
}) => {
  const [ connectId, setConnectId ] = useState(null)
  const [ apiKey, setApiKey ] = useState(null)
  const [ initLoading, setInitLoading ] = useState(true)
  const [ errors, setErrors ] = useState(null)

  // Fetch the Chariot Connect cid + api_key from our backend, keyed
  // by the org's EIN. Mirrors v6/Daf.jsx.
  useEffect(() => {
    if (partnerItem.beneficiary.id == '_multiple') return

    const ein = partnerItem.beneficiary.ein.replace(/-/g, '')
    setInitLoading(true)

    fetch(dafProps.initUrl, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ ein }),
    })
    .then(response => response.json().then(data => {
      if (response.ok) {
        setConnectId(data.connect_id)
        setApiKey(data.api_key)
      } else {
        setErrors({ base: [ i18n.t('ajax_error') ] })
      }
    }))
    .catch(error => {
      console.error('Chariot init fetch error:', error)
      setErrors({ base: [ i18n.t('ajax_error') ] })
    })
    .finally(() => setInitLoading(false))
  }, [ partnerItem.beneficiary.id ])

  // ChariotConnect requires onDonationRequest per its types. We don't
  // collect donor data ahead of the workflow, so this is empty — the
  // donor enters everything inside Chariot.
  const onDonationRequest = () => ({})

  const onSuccess = async chariotResponse => {
    setSubmitInFlight(true)
    const user = chariotResponse.detail.user
    const grantIntent = chariotResponse.detail.grantIntent
    const workflowSessionId = chariotResponse.detail.workflowSessionId

    try {
      const { errors: draftErrors, token } = await record.saveDraft({
        principalOnly: true,
        order: { payment_source: 'daf' },
      })
      if (draftErrors) {
        // Chariot already captured the grantIntent. We're halting
        // before ChariotApi.create_grant runs, so the DAF gift won't
        // actually send. Show the donor the validation error and
        // let them retry — better than silently letting the
        // grantIntent dangle.
        setErrors(draftErrors)
        setSubmitInFlight(false)
        return
      }

      const response = await fetch(dafProps.submitUrl, {
        method: 'POST',
        headers: csrfHeaders(),
        body: JSON.stringify({
          order_id: token,
          api_key: apiKey,
          workflow_session_id: workflowSessionId,
          amount: grantIntent.amount,
          order: {
            user_first_name: user?.firstName,
            user_last_name: user?.lastName,
            email: user?.email || 'tech@pledgeling.com',
            donation_amount: grantIntent.amount / 100,
          },
        }),
      })

      if (!response.ok) {
        throw new Error('Server side validation failed', {
          cause: Error(JSON.stringify(await response.json(), null, 2)),
        })
      }

      const data = await response.json()
      finished({
        ...data.order,
        token: data.token,
        partnerItem,
      })
    } catch (error) {
      // .catch only fires on network/JSON failure (response.ok handles
      // 4xx/5xx). We genuinely don't know whether ChariotApi.create_grant
      // ran — request may never have reached our server, or completed
      // but only the response was lost. donation_unconfirmed copy
      // commits to neither outcome to avoid prompting a donor retry
      // that could duplicate the grantIntent.
      console.error(error)
      Honeybadger.notify(error, {
        name: 'DafPostSuccessError',
        context: { workflowSessionId },
      })
      setErrors({ base: [ i18n.t('errors.donation_unconfirmed') ] })
      setSubmitInFlight(false)
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader heading={i18n.t('steps.payment.heading')} />

      <DonationExtras
        extras={extras}
        i18n={i18n}
        blank_modal_src={blank_modal_src}
        emoji_picker_loader_src={emoji_picker_loader_src}
        widget_instance_token={widget_instance_token}
      />

      {initLoading && (
        <div className="flex items-center justify-center min-h-[48px]">
          <FontAwesomeIcon icon={faSpinner} pulse className="text-gray-500" />
        </div>
      ) || connectId && (
        <ChariotConnect
          cid={connectId}
          onDonationRequest={onDonationRequest}
          onSuccess={onSuccess}
        />
      )}

      {errors?.base?.[0] && (
        <p
          role="alert"
          className="rounded border border-danger-300 bg-danger-50 text-danger-800 p-3 text-sm"
        >
          {errors.base[0]}
        </p>
      )}

      <div className="text-sm text-center bg-gray-100 p-3 rounded-lg">
        <p className="max-w-72 mx-auto">
          {i18n.t('chariot.heading')}<br/>
          <a
            href="https://help.givechariot.com/dafpay/editor"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            {i18n.t('chariot.learn_more')}
          </a>
        </p>
      </div>

      <BackLink i18n={i18n} onClick={onBack} />
    </div>
  )
}

export default DafPayment
